import {
  LINE_CHART_OPTIONS,
  NONE,
  TOTAL,
} from '../../../common/utils/constants'
import {
  Breakpoint,
  ChartElements,
  chartTypes,
  disaggregations,
  MissingDataLineTypes,
  periodicity,
} from '../../../common/utils/enum'
import {
  createDottedLineZonesForNulls,
  fetchCountryMappingForColor,
  fetchSeriesDataWithMarker,
  formatNumberOnUnitMeasure,
  formatUnitMeasure,
  getBadges,
  getNameFromDisaggregation,
  formatValues,
  applyAxisRange,
  getUnitMeasureItems,
} from '../../../common/utils/helper'
import { COLOR_MAP } from '../../../common/utils/theme'
import { getBenchmarkList, getChartTitle } from '../common/helper'

const getLineChartSubtitle = (
  countries,
  selectedMinYear,
  selectedMaxYear,
  badges
) => {
  const formattedCountries = countries.join(', ')
  const subtitle = formattedCountries
    ? selectedMinYear && selectedMaxYear
      ? selectedMinYear !== selectedMaxYear
        ? `${formattedCountries}, ${selectedMinYear}-${selectedMaxYear}`
        : `${formattedCountries}, ${selectedMinYear}`
      : formattedCountries
    : selectedMinYear && selectedMaxYear
      ? selectedMinYear !== selectedMaxYear
        ? `${selectedMinYear}-${selectedMaxYear}`
        : selectedMinYear
      : ''

  let badgesDiv =
    badges && badges.length > 0
      ? badges.map(
          (badge) =>
            `<div class="text tui_badge" style="font-size: 0.8125rem;font-weight: 600;line-height: 1.0625rem;text-transform: uppercase;border-radius: 2px;background: #F4F5F7;border: 1px solid #CED4DE;color: #535B6A;padding: 4px 10px;">
        <div>
          <p>${badge}</p>
        </div>
      </div>`
        )
      : []
  badgesDiv = badgesDiv.join(' ')
  return {
    useHTML: true,
    text: `<p class="tui__text_base">${subtitle}</p>
      <div class="tui__badge_container" style="margin: 0 0 0.75rem;display: flex;align-items: center;flex-wrap: wrap;">
        <div class="tui_badge_group" style="display: flex;align-items: center;flex-wrap: wrap;">
            ${badgesDiv}
        </div>
      </div>
    `,
    align: 'left',
  }
}

export const generateLineChartOptions = (
  indicatorData,
  indicatorMetadata,
  id,
  disaggregationCountries,
  selectedCountries,
  selectedMinYear,
  selectedMaxYear,
  selectedComparison,
  disaggregationData,
  selectedFilters,
  selectedUnitMeasure,
  filteredAggregateData,
  countryHierarchy,
  selectedFrequency,
  legendRef,
  unitMeasureName,
  isMobile,
  unitMultName = '',
  selectedAverages,
  regionCountriesMapping,
  unitMeasureList,
  site
) => {
  let minCategory, maxCategory
  const unitMeasureItems = getUnitMeasureItems(disaggregationData)
  const shortLabel = unitMeasureItems?.find(
    (item) => item.code === selectedUnitMeasure
  )?.short_label

  if (selectedFrequency === periodicity.Daily) {
    minCategory = selectedMinYear
    maxCategory = selectedMaxYear
  } else {
    minCategory = parseInt(selectedMinYear)
    maxCategory = parseInt(selectedMaxYear)
  }

  // Additional visualization: missing data logic -- start
  let missingDataType = ''
  if (
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.missing_data
      ?.length &&
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.missing_data[0]
      ?.line
  ) {
    missingDataType =
      indicatorMetadata?.admin_metadata?.[site]?.visualization?.missing_data[0]
        .line
  } else {
    missingDataType = MissingDataLineTypes.ShowBreaks
  }
  // Additional visualization: missing data logic -- end

  let filteredCategories = []
  let frequency =
    indicatorData && indicatorData.length > 0
      ? indicatorData[0]?.[disaggregations.Frequency]
      : ''
  const mapping = fetchCountryMappingForColor(
    regionCountriesMapping,
    selectedCountries
  )

  let finalIndicatorData = [...indicatorData]
  selectedCountries.forEach((item) => {
    if (!indicatorData.find((data) => data[disaggregations.RefArea] === item)) {
      finalIndicatorData.push({ [disaggregations.RefArea]: item })
    }
  })

  const selectedCountriesData = finalIndicatorData
    .filter((data) => selectedCountries.includes(data[disaggregations.RefArea]))
    .map((data) => {
      const allCategories = data?.data?.xAxis
      const startIndex = allCategories?.findIndex((category) =>
        selectedFrequency === periodicity.Daily
          ? category >= minCategory
          : parseInt(category) >= minCategory
      )

      const endIndex = allCategories?.findLastIndex((category) =>
        selectedFrequency === periodicity.Daily
          ? category >= maxCategory
          : parseInt(category) === maxCategory
      )
      filteredCategories = allCategories?.slice(startIndex, endIndex + 1)

      const filteredData = data?.data?.yAxis
        .slice(startIndex, endIndex + 1)
        .map((value) =>
          value && value !== 'null' && value !== 'NaN'
            ? formatValues(value)
            : null
        )

      const countryName = disaggregationCountries?.find(
        (item) => item.code === data[disaggregations.RefArea]
      )?.description
      let seriesName = countryName
      // color scheme logic -- start
      let color = ''
      if (selectedComparison === NONE) {
        const regionData = mapping.find((item) =>
          item.countries.includes(data[disaggregations.RefArea])
        )
        const region = regionData?.id
        const index = regionData?.countries.indexOf(
          data[disaggregations.RefArea]
        )
        // the modulus number(4) is to decide the color repetition
        if (region && index >= 0) {
          color = COLOR_MAP[`${region}_countries`][index % 4]
        }
      } else {
        if (selectedComparison?.startsWith('COMP_BREAKDOWN')) {
          // const filters = selectedFilters[selectedComparison].split(',')
          // const index = filters.indexOf(data[selectedComparison])
          if (data[selectedComparison] === TOTAL) {
            color = COLOR_MAP[data[selectedComparison]]
          } else {
            let customDimensions = disaggregationData?.dimensions?.find(
              (item) => item.field_name === selectedComparison
            )?.field_value
            customDimensions = customDimensions.filter(
              (item) => item.code !== TOTAL
            )
            const index = customDimensions.findIndex(
              (item) => item.code === data[selectedComparison]
            )
            color = COLOR_MAP['category'][index]
          }
        } else if (selectedComparison.toLowerCase() === 'age') {
          if (data[selectedComparison] === TOTAL) {
            color = COLOR_MAP[data[selectedComparison]]
          } else {
            let ages = disaggregationData?.dimensions?.find(
              (item) => item.field_name?.toLowerCase() === 'age'
            )?.field_value
            ages = ages.filter((item) => item.code !== TOTAL)
            const index = ages.findIndex(
              (item) => item.code === data[selectedComparison]
            )
            color = COLOR_MAP['age'][index % 5]
          }
        } else {
          color = COLOR_MAP[data[selectedComparison]]
        }
      }
      // color scheme logic -- end
      if (selectedComparison !== NONE) {
        seriesName = `${countryName} ${getNameFromDisaggregation(
          disaggregationData?.dimensions,
          selectedComparison,
          data[selectedComparison]
        )}`
      }

      let seriesObject = {
        name: seriesName,
        data: filteredData ? fetchSeriesDataWithMarker(filteredData) : [],
        color: color,
        lineWidth: 4,
        unitMeasureCode: data?.[disaggregations.UnitMeasure],
      }
      // Additional visualization: missing data logic -- start
      if (missingDataType === MissingDataLineTypes.ShowBreaks) {
        seriesObject.zoneAxis = 'x'
        seriesObject.zones = createDottedLineZonesForNulls(filteredData)
      }
      // Additional visualization: missing data logic -- end
      return seriesObject
    })
  const countries = selectedCountries.map((country) => {
    const countryName = disaggregationCountries?.find(
      (item) => item.code === country
    )?.description
    return countryName
  })
  const badges = getBadges(
    selectedFilters,
    disaggregationData,
    selectedComparison,
    unitMeasureName
  )

  filteredAggregateData.forEach((data) => {
    const allCategories = data.data.xAxis
    const startIndex = allCategories?.findIndex((category) =>
      selectedFrequency === periodicity.Daily
        ? category >= minCategory
        : parseInt(category) >= minCategory
    )
    const endIndex = allCategories?.findLastIndex((category) =>
      selectedFrequency === periodicity.Daily
        ? category >= maxCategory
        : parseInt(category) === maxCategory
    )
    filteredCategories = allCategories?.slice(startIndex, endIndex + 1)

    const filteredData = data.data.yAxis
      .slice(startIndex, endIndex + 1)
      .map((value) =>
        value && value !== 'null' && value !== 'NaN'
          ? formatValues(value)
          : null
      )

    let seriesName = '',
      color = ''
    seriesName =
      selectedComparison === NONE
        ? `${getNameFromDisaggregation(
            disaggregationData?.dimensions,
            disaggregations.RefArea,
            data[disaggregations.RefArea]
          )}`
        : `${getNameFromDisaggregation(
            disaggregationData?.dimensions,
            disaggregations.RefArea,
            data[disaggregations.RefArea]
          )} ${getNameFromDisaggregation(
            disaggregationData?.dimensions,
            selectedComparison,
            data[selectedComparison]
          )}`
    // color scheme logic -- start
    if (selectedComparison === NONE) {
      color = COLOR_MAP[data[disaggregations.RefArea]]
    } else {
      color = COLOR_MAP[data[selectedComparison]]
      // color = undefined
    }
    // color scheme logic -- start

    let seriesObject = {
      name: seriesName,
      data: filteredData ? fetchSeriesDataWithMarker(filteredData) : [],
      color,
      lineWidth: indicatorData?.length > 0 ? 1.5 : 2,
      opacity: 1,
      zIndex: 1,
      unitMeasureCode: data?.[disaggregations.UnitMeasure],
    }
    // Additional visualization: missing data logic -- start
    if (missingDataType === MissingDataLineTypes.ShowBreaks) {
      seriesObject.zoneAxis = 'x'
      seriesObject.zones = createDottedLineZonesForNulls(filteredData)
    }
    // Additional visualization: missing data logic -- end
    selectedCountriesData.push(seriesObject)
  })
 
  const totalCategories = filteredCategories?.length
  let tickPositions = []
  if (isMobile) {
    const maxLabels = 3
    const interval = Math.ceil((totalCategories - 1) / (maxLabels - 1)) || 1
    for (let i = 0; i < totalCategories; i += interval) {
      tickPositions.push(i)
    }
    if (tickPositions[tickPositions.length - 1] !== totalCategories - 1) {
      tickPositions.push(totalCategories - 1)
    }
  } else {
    const maxLabels = frequency === periodicity.Annual ? 10 : 8
    const interval = Math.ceil((totalCategories - 1) / (maxLabels - 1)) || 1
    for (let i = 0; i < totalCategories; i += interval) {
      tickPositions.push(i)
    }
    if (tickPositions[tickPositions.length - 1] !== totalCategories - 1) {
      tickPositions.push(totalCategories - 1)
    }
  }

  if (selectedAverages && selectedAverages.length > 0) {
    let averages = getBenchmarkList({
      selectedAverages,
      selectedCountries,
      countryHierarchy,
      chartType: chartTypes.Trend,
      disaggregationCountries,
    })
    averages = averages.filter((item) =>
      disaggregationCountries.find((country) => country.code === item)
    )
    averages.forEach((average) => {
      const aggregateData = filteredAggregateData.find(
        (data) => data[disaggregations.RefArea] === average
      )
      if (!aggregateData) {
        const seriesName = `${getNameFromDisaggregation(
          disaggregationData?.dimensions,
          disaggregations.RefArea,
          average
        )}`
        selectedCountriesData.push({
          name: seriesName,
          data: [],
          color: '#8A969F',
          lineWidth: 1.5,
          opacity: 0.5,
          showInLegend: true,
        })
      }
    })
  }

  const lineChartOptions = {
    ...LINE_CHART_OPTIONS,
    chart: {
      // height: 600,
      events: {
        load: function () {
          const chart = this
          const series = chart.series
          if (legendRef.current) {
            // Create and append legend items
            let maxLength = series.reduce(
              (max, item) => Math.max(max, item.name.length),
              0
            )
            // Clear the custom legend container
            legendRef.current.innerHTML = ''

            legendRef.current.className = 'trend-graph-legend-wrapper'

            const calculatedWidth = maxLength * 10 + 8
            const containerDiv = document.getElementById('trend_white_box')
            const containerWidth = containerDiv
              ? containerDiv.offsetWidth - 40
              : calculatedWidth
            const minMaxWidth = Math.min(calculatedWidth, containerWidth)

            legendRef.current.style = `grid-template-columns: repeat(auto-fit, minmax(${minMaxWidth}px, auto));`

            // Define shapes based on index
            const shapes = [
              'circle',
              'diamond',
              'square',
              'triangle',
              'reverse_triangle',
            ]

            series.forEach((item, index) => {
              const legendItem = document.createElement('div')

              const shape = document.createElement('span')
              // Set shape based on the index

              switch (shapes[index % shapes.length]) {
                case 'triangle':
                  shape.className = 'shape-triangle'
                  shape.style.borderBottom = `8px solid ${item.color}`
                  shape.style.backgroundColor = `${item.color}` // Use the item's color here
                  break
                case 'reverse_triangle':
                  shape.className = 'shape-reverse_triangle'
                  shape.style.borderBottom = `8px solid ${item.color}`
                  shape.style.backgroundColor = `${item.color}`

                  break
                case 'circle':
                  shape.className = 'shape-circle'
                  shape.style.backgroundColor = item.color // Set the shape color
                  break
                case 'square':
                  shape.className = 'shape-square'
                  shape.style.backgroundColor = item.color // Set the shape color
                  break
                case 'diamond':
                  shape.className = 'shape-diamond'
                  shape.style.backgroundColor = item.color // Set the shape color
                  break
                default:
                  shape.className = 'shape-circle'
                  shape.style.backgroundColor = item.color
              }

              // Use DOM methods (textContent) instead of innerHTML to prevent XSS from API-sourced data.
              const nameEl = document.createElement('b')
              nameEl.style.fontSize = '12px'
              nameEl.style.fontWeight = '600'
              nameEl.textContent = item.name.toUpperCase() + ' '
              legendItem.appendChild(nameEl)
              if (item.data.length === 0) {
                const noDataEl = document.createElement('span')
                noDataEl.style.fontWeight = 'normal'
                noDataEl.style.fontSize = '12px'
                noDataEl.textContent = '\u00a0(No Data)'
                legendItem.appendChild(noDataEl)
              }

              legendItem.className = 'trend-graph-legend'

              legendItem.onclick = () => {
                item.setVisible(!item.visible)
                // Always disable markers
                item.update({ marker: { enabled: false } })
                // Set marker visibility to false when legend is clicked

                const strongElement = legendItem.querySelector('strong')
                if (strongElement) {
                  strongElement.style.textDecoration = item.visible
                    ? ''
                    : 'line-through'
                  strongElement.style.color = item.visible ? 'black' : 'gray'
                }
              }
              legendItem.prepend(shape) // Add shape to the legend item
              legendRef.current.appendChild(legendItem)
            })
          }
        },
        redraw: function () {
          let axisLabelTextElements = []
          axisLabelTextElements = document
            .getElementById('lineChart_container'
            )
            ?.querySelectorAll('g.highcharts-xaxis-labels>text')

          if (axisLabelTextElements && axisLabelTextElements.length > 0) {
            const lastLabel =
              axisLabelTextElements[axisLabelTextElements.length - 1]
            const secondLastLabel =
              axisLabelTextElements[axisLabelTextElements.length - 2]
            const textWidth = secondLastLabel?.getBoundingClientRect().width
            if (
              lastLabel?.getAttribute('x') -
                secondLastLabel?.getAttribute('x') <
              textWidth
            ) {
              secondLastLabel.style.display = 'none'
            }
          }
        },
      },
    },
    title: getChartTitle(
          indicatorMetadata?.admin_metadata?.[site]?.visualization
            ?.display_name || indicatorMetadata?.series_description.name
        ),
    subtitle: getLineChartSubtitle(countries, minCategory, maxCategory, badges),
    xAxis: {
      categories: filteredCategories,
      tickPositions: tickPositions,
      labels: {
        style: { color: 'rgba(0,0,0,0.6)', whiteSpace: 'nowrap' },
      },
      lineColor: 'transparent',
      gridLineColor: 'transparent',
    },
    yAxis: {
      title: { text: null },
      gridLineColor: COLOR_MAP[ChartElements.GridLine],
      labels: {
        style: { color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.9rem' },
        formatter: function () {
          return formatNumberOnUnitMeasure({
            value: this.value,
            unitMeasure: selectedUnitMeasure,
            decimals:
              indicatorData[0]?.[disaggregations.Decimals] ||
              filteredAggregateData?.[0]?.[disaggregations.Decimals],
            axis: this.axis,
          })
        },
      },
      plotLines: [
        {
          value: 0,
          color: COLOR_MAP[ChartElements.ZeroLine],
          zIndex: 1,
        },
      ],
    },
    series: selectedCountriesData,
    exporting: {
      enabled: false,
    },
    tooltip: {
      ...LINE_CHART_OPTIONS.tooltip,
      backgroundColor: null,
      borderWidth: 0,
      shadow: false,
      useHTML: true,
      style: {
        padding: 0,
      },
      formatter: function () {
        const title = `<div class="tui_chart_tooltip_title" style="font-size: 14px; font-weight: 600; line-height: 16px; color: rgba(0, 0, 0, 0.87); padding-bottom: 4px; margin-bottom: 4px; border-bottom: 1px solid #CED4DE;">${this.x}</div>`

        let points = this.points
          ?.map((point) => {
            let symbol = ''
            let symbolStyle = `font-size: 17px; color: ${point.color}; margin-right: 8px; vertical-align: middle; display: inline-block;`

            if (point.series.symbol) {
              switch (point.series.symbol) {
                case 'circle':
                  symbol = '●'
                  break
                case 'diamond':
                  symbol = '♦'
                  break
                case 'square':
                  symbol = '■'
                  break
                case 'triangle':
                  symbol = '▲'
                  break
                case 'triangle-down':
                  symbol = '▼'
                  break
                default:
                  symbol = '●'
              }

              if (
                point.series.symbol === 'triangle' ||
                point.series.symbol === 'triangle-down' ||
                point.series.symbol === 'square'
              ) {
                symbolStyle = `font-size: 12px; color: ${point.color}; margin-right: 8px; vertical-align: middle; display: inline-block;`
              }

              symbol = `<span style="${symbolStyle}">${symbol}</span>`
            } else if (point.series.marker && point.series.marker.symbol) {
              const url = point.series.marker.symbol.replace(/^url\(|\)$/g, '')
              symbol = `<img src="${url}" alt="Marker" style="height: 10px; width: 10px; margin-right: 8px; vertical-align: middle;" />`
            }

            return `
          <li style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 4px;">
            <div class="tooltip_country" style="display: flex; align-items: center; min-width: 150px; padding-right: 10px;">
              ${symbol}
              ${point.series.name}
            </div>
            <span class="tooltip_value" style="font-size: 16px; font-weight: 700; line-height: 20px; color: rgba(0, 0, 0, 0.87);">
              ${formatNumberOnUnitMeasure({
                value: point.y,
                unitMeasure:
                  selectedUnitMeasure ||
                  point?.series?.userOptions?.unitMeasureCode ||
                  '',
                decimals:
                  indicatorData?.[0]?.[disaggregations.Decimals] ||
                  filteredAggregateData?.[0]?.[disaggregations.Decimals],
                isCommaSeparatedFormat: true,
              })} ${unitMultName} ${formatUnitMeasure(
                selectedUnitMeasure ||
                  point?.series?.userOptions?.unitMeasureCode ||
                  '',
                unitMeasureName ||
                  unitMeasureList.find(
                    (item) =>
                      item.code === point?.series?.userOptions?.unitMeasureCode
                  )?.description ||
                  '',
                shortLabel ||
                  unitMeasureList.find(
                    (item) =>
                      item.code === point?.series?.userOptions?.unitMeasureCode
                  )?.short_label ||
                  ''
              )}
            </span>
          </li>
        `
          })
          .join('')

        return `
      <div class="tui_chart_tooltip" style="border: 0.5px solid #CED4DE; background: #FFF; padding: 6px 8px; box-shadow: 4px 4px 4px 0px rgba(0, 0, 0, 0.15); width: auto;">
        ${title}
        <ul style="padding-left: 0; list-style: none; margin: 0;">
          ${points}
        </ul>
      </div>
    `
      },
    },
    legend: {
      enabled: false,
    },
    responsive: {
      ...LINE_CHART_OPTIONS.responsive,
      rules: [
        {
          condition: {
            minWidth: Breakpoint.Mobile,
          },
          chartOptions: {
            xAxis: {
              tickPositions: tickPositions,
            },
          },
        },
      ],
    },
    plotOptions: {
      ...LINE_CHART_OPTIONS.plotOptions,
      series: {
        ...LINE_CHART_OPTIONS.plotOptions.series,
        connectNulls: true,
      },
    },
  }
  applyAxisRange({
    visualizationMetadata:
      indicatorMetadata?.admin_metadata?.[site]?.visualization,
    chartOptions: lineChartOptions,
    axisKey: 'yAxis',
    axisDimension: 'y',
  })

  return lineChartOptions
}
